import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Compass, 
  FileCheck, 
  Calculator, 
  HardHat, 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  Layers, 
  Home, 
  Trees, 
  Hammer, 
  ChevronRight,
  Send,
  PhoneCall,
  MapPin,
  LandPlot,
  Users2,
  FileText,
  Download,
  ExternalLink,
  Maximize2,
  Eye,
  Ruler,
  Sparkles
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactSection from '../components/ContactSection';
import GalleryLightboxModal from '../components/GalleryLightboxModal';
import { SITE_INFO } from '../data';
import { imageUrl } from '../lib/images';

export default function ProjectDevelopmentPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Lightbox state for visualisations and craftsmanship photos
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const projectVisualizations = [
    {
      title: "Gaderum og forhaver (3D Visualisering)",
      description: "Moderne étplans rækkehuse i gule mursten med sorte tage, integrerede carporte/udhuse og overskuelige forhaver mod den nyanlagte boligvej.",
      badge: "Gadefacade / Forhave",
      src: "/_assets/forside-rev-3_81597a46.png"
    },
    {
      title: "Terrasser mod grønt areal (3D Visualisering)",
      description: "Private have- og terrassezoner mod vest/syd med direkte udgang til rekreative, fælles græsområder og beplantning.",
      badge: "Haveside / Terrasser",
      src: "/_assets/bagside-rev-2_1570c7fa.png"
    }
  ];

  const projectDrawings = [
    {
      id: "A010",
      title: "Situationsplan",
      format: "Mål 1:500 (A2)",
      description: "Overordnet disponering af hele grunden på 14.000 m²: 28 matrikler, boligvej, 6 fælles gæsteparkeringspladser, vendeplads og 2.617 m² friareal.",
      fileUrl: "/_assets/a010-situationsplan_771502b9.pdf",
      specs: [
        "14.000 m² samlet grundareal",
        "28 boligparceller",
        "2.617 m² fælles friareal"
      ]
    },
    {
      id: "A110",
      title: "Grundplan – Stueplan",
      format: "Mål 1:50 (A3)",
      description: "Gennemtænkt étplans boligindretning på 129–133 m² med 31 m² opholdsstue, 22 m² køkken-alrum, forældresoveværelse, to værelser, to badeværelser og bryggers.",
      fileUrl: "/_assets/a110-grundplan-stueplan_45e97ec0.pdf",
      specs: [
        "129–133 m² boligareal",
        "Køkken-alrum & stue",
        "3 værelser & 2 badeværelser"
      ]
    },
    {
      id: "A111",
      title: "Grundplan m. Skur",
      format: "Mål 1:50 (A3)",
      description: "Detaljeret plan der dokumenterer integration af det tilhørende udhus/redskabsskur, overdækket ankomstparti samt flisebelagte stier.",
      fileUrl: "/_assets/a111-grundplan-m-skur_1decb7d2.pdf",
      specs: [
        "Integreret redskabsskur",
        "Overdækket ankomst",
        "Præcise adgangsforhold"
      ]
    },
    {
      id: "A200",
      title: "Facader Nord – Syd",
      format: "Mål 1:75 (A3)",
      description: "Arkitektoniske opstalter for nord- og sydfacader med gule mursten, sorte tagflader, proportioner samt generøse vinduespartier mod haven.",
      fileUrl: "/_assets/a200-facade-n-s_db0271cf.pdf",
      specs: [
        "Nord- og sydfacader",
        "Store lysindfald mod terrasse",
        "Klassisk dansk murværk"
      ]
    },
    {
      id: "A300",
      title: "Bygningssnit A-A",
      format: "Mål 1:50 (A3)",
      description: "Tværsnit der viser bygningskroppen med karakteristisk asymmetrisk taghældning (13° mod have og 30° mod gade), 2,50 m lofthøjde og terrænovergange.",
      fileUrl: "/_assets/a300-snit-a-a_9abf1a8d.pdf",
      specs: [
        "Taghældning 13° & 30°",
        "2,50 m indvendig lofthøjde",
        "Høj byggeteknisk detaljering"
      ]
    }
  ];

  const timberPhotos = [
    {
      title: "Køkken-alrum med synlige træbjælker",
      description: "Lyst og åbent rum med fritlagte loftsbjælker, skandinavisk snedkerkøkken og masser af naturligt dagslys.",
      src: "/_assets/img-1363_18e53513.webp"
    },
    {
      title: "Snedkerkøkken i naturtræ med buet vindue",
      description: "Unikke træoverflader, minimalistisk køkkenø med vask og et karakterfuldt buet vinduesparti mod det grønne.",
      src: "/_assets/img-1362_a324bcdc.webp"
    },
    {
      title: "Arkitektoniske detaljer & skifertag",
      description: "Kombination af gennemført indvendigt træarbejde og udvendigt skifertag med elegant svungen kvist.",
      src: "/_assets/img-1361_a651616d.webp"
    },
    {
      title: "Åben stue med rustikke bjælkekonstruktioner",
      description: "Gennemført tømrerarbejde med synlige konstruktionstræer, lyst trægulv og rummelig opdeling.",
      src: "/_assets/img-1360_5e6ad591.webp"
    },
    {
      title: "Harmonisk opholdsmiljø & nordisk kvalitet",
      description: "Eksklusive materialevalg og sammenhængende overgange mellem køkken, spiseplads og opholdsstue.",
      src: "/_assets/img-1359_bf222424.webp"
    }
  ];

  const servicesList = [
    {
      title: "Udvikling af jord og byggegrunde",
      icon: LandPlot,
      desc: "Analyse af potentiale, jordbund og byggemuligheder tilpasset terræn og omgivelser."
    },
    {
      title: "Udstykning og udvikling af boligområder",
      icon: MapPin,
      desc: "Strukturerede planer for infrastruktur, grundopdeling og helhedsorienterede boligkvarterer."
    },
    {
      title: "Projekter med flere boliger",
      icon: Building2,
      desc: "Erfaring med flerboligbyggeri, tæt-lav bebyggelse og samlede boligenheder."
    },
    {
      title: "Opførelse af typehuse",
      icon: Home,
      desc: "Effektive byggeprocesser med gennemprøvede løsninger og kompromisløs kvalitet."
    },
    {
      title: "Arkitekttegnede villaer",
      icon: Compass,
      desc: "Skræddersyede enfamiliehuse med unikke detaljer, trækonstruktioner og karakter."
    },
    {
      title: "Rækkehuse og mindre boligprojekter",
      icon: Layers,
      desc: "Attraktive rækkehuse og klyngebebyggelser med fokus på gode uderum og fællesskab."
    },
    {
      title: "Sommerhuse og fritidsboliger",
      icon: Trees,
      desc: "Eksklusive fristeder i pagt med naturen med fokus på holdbare nordiske materialer."
    },
    {
      title: "Ombygning og udvikling af eksisterende ejendomme",
      icon: Hammer,
      desc: "Transformation og modernisering af eksisterende bygninger med respekt for historien."
    },
    {
      title: "Projektøkonomi og budgetter",
      icon: Calculator,
      desc: "Realistiske kalkuler, gennemskuelige budgetter og fastprisaftaler uden overraskelser."
    },
    {
      title: "Koordinering med arkitekt, ingeniør og øvrige rådgivere",
      icon: Users2,
      desc: "Sammenhæng mellem kreative visioner, statik, energi og byggeteknisk virkelighed."
    },
    {
      title: "Myndighedsproces og byggesag",
      icon: FileCheck,
      desc: "Håndtering af byggeansøgninger, lokalplaner, dispensationer og kommunal dialog."
    },
    {
      title: "Totalentreprise og opførelse",
      icon: HardHat,
      desc: "Én samlet ansvarlig partner fra første spadestik til nøglefærdig aflevering."
    }
  ];

  const phases = [
    {
      step: "01",
      title: "Idé, Grundanalyse & Forundersøgelse",
      duration: "Typisk 2-4 uger",
      description: "Enhver succesfuld projektudvikling starter med en grundig afdækning af potentiale og rammer. Vi analyserer grundens beskaffenhed, lokalplaner, byggeret, servitutter og jordbundsforhold.",
      deliverables: [
        "Screening af lokalplan og byggeretlige rammer",
        "Vurdering af byggegrund og eksisterende konstruktioner",
        "Overordnet budgetramme og tidsmæssig feasibility",
        "Første idémøde og forventningsafstemning"
      ]
    },
    {
      step: "02",
      title: "Skitsering & Arkitektsamarbejde",
      duration: "Typisk 4-8 uger",
      description: "Vi samarbejder tæt med visionære arkitekter og ingeniører for at forme det arkitektoniske udtryk. G28 bidrager fra første streg med byggeteknisk sparring, så design og bygbarhed følges ad.",
      deliverables: [
        "Volumenstudier, facadetegninger og rumlige dispositioner",
        "Materialestrategi (træsorter, facadetyper, energieffektivitet)",
        "Tidlig byggeteknisk optimering for at undgå fordyrende løsninger",
        "Præsentation af visualiseringer og 3D-skitser"
      ]
    },
    {
      step: "03",
      title: "Myndighedsprojekt & Byggetilladelse",
      duration: "Typisk 6-12 uger",
      description: "Vi udarbejder det fulde myndighedsmateriale og styrer dialogen med den pågældende kommune. Vi sikrer, at alle krav til energiramme, statik, brand og miljø overholdes til punkt og prikke.",
      deliverables: [
        "Udarbejdelse og indsendelse af byggeansøgning",
        "Håndtering af eventuelle dispensationer og nabohøringer",
        "Statisk dokumentation og brandteknisk redegørelse",
        "Godkendt byggetilladelse fra kommunen"
      ]
    },
    {
      step: "04",
      title: "Detailprojektering & Fastprisaftale",
      duration: "Typisk 3-6 uger",
      description: "Før det første spadestik tages, låses alle detaljer. Vi udarbejder arbejdstegninger, fastlægger leverancer med vores underleverandører og indgår en bindende entrepriseaftale med fast tidsplan.",
      deliverables: [
        "Detaljerede arbejdstegninger og installationsplaner",
        "Gennemskuelig entreprisekontrakt (AB92 / AB18)",
        "Låst tidsplan med faste milepæle",
        "Endelig materialebestilling og logistikkoordinering"
      ]
    },
    {
      step: "05",
      title: "Udførelse, Byggeledelse & Nøglefærdig Aflevering",
      duration: "Efter aftalt tidsplan",
      description: "Under selve byggeprocessen har du én fast byggeleder fra G28. Vi koordinerer alle faggrupper, afholder faste byggemøder og sikrer kompromisløs kvalitet frem til nøgleoverdragelsen.",
      deliverables: [
        "Daglig byggeledelse og koordinering af alle fagdiscipliner",
        "Løbende fotodokumentation og referater fra byggemøder",
        "Grundig før-aflevering og udbedring af eventuelle punkter",
        "Nøglefærdig overdragelse med drift- og vedligeholdelsesmappe"
      ]
    }
  ];

  return (
    <>
      <SEOHead 
        title={`Projektudvikling – Fra jord til færdige boliger - ${SITE_INFO.name}`}
        description="Hos G28 ApS hjælper vi med at udvikle jord og ejendomme til konkrete byggeprojekter – fra de første idéer og projekttegninger til færdige boliger."
      />

      {/* Hero Header */}
      <section className="relative bg-stone-900 text-white py-20 sm:py-28 overflow-hidden border-b border-stone-800">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src={imageUrl('/_assets/forside-rev-3_81597a46.png', 'width=1920,quality=85,format=auto')}
            alt="Projektudvikling af boligområde og moderne rækkehuse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/90 to-stone-900/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold tracking-wider uppercase">
              <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Projektudvikling & Totalentreprise</span>
            </div>

            <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl text-white tracking-tight uppercase leading-[1.1]">
              FRA JORD TIL FÆRDIGE BOLIGER
            </h1>

            <p className="text-stone-200 text-lg sm:text-xl font-medium leading-relaxed">
              Har du en grund, en større ejendom eller et stykke jord med udviklingsmuligheder?
            </p>

            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              Hos <strong className="text-white font-bold">G28 ApS</strong> hjælper vi med at udvikle jord og ejendomme til konkrete byggeprojekter – fra de første idéer, situationsplaner og projekttegninger til nøglefærdige boliger.
            </p>

            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#aktuelt-projekt"
                className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-sm sm:text-base px-6 py-3.5 shadow-lg transition-all"
              >
                <span>Se aktuelt projekt & tegninger</span>
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>

              <a
                href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-700 text-white border border-stone-700 font-heading font-semibold text-sm sm:text-base px-6 py-3.5 transition-all"
              >
                <PhoneCall className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <span>Ring {SITE_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Highlights Bar */}
      <section className="bg-stone-950 border-b border-stone-800 py-6 text-stone-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center divide-y md:divide-y-0 md:divide-x divide-stone-800">
            <div className="pt-2 md:pt-0">
              <span className="block font-heading font-extrabold text-2xl text-white">Fra idé til nøgle</span>
              <span className="text-xs text-stone-400 uppercase tracking-wider">Komplet forløb & tegninger</span>
            </div>
            <div className="pt-2 md:pt-0 md:pl-4">
              <span className="block font-heading font-extrabold text-2xl text-white">Totalentreprise</span>
              <span className="text-xs text-stone-400 uppercase tracking-wider">Én ansvarlig partner</span>
            </div>
            <div className="pt-2 md:pt-0 md:pl-4">
              <span className="block font-heading font-extrabold text-2xl text-white">Fast aftale</span>
              <span className="text-xs text-stone-400 uppercase tracking-wider">Tidsplan & budget</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Context Section */}
      <section className="py-16 sm:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-800">
                Udvikling & Realisering
              </div>

              <div className="border-l-4 border-amber-700 pl-6 py-2">
                <p className="text-stone-900 text-lg sm:text-xl font-normal leading-relaxed">
                  Det kan være <strong className="font-bold text-stone-950">udstykning af jord, udvikling af boligområder, opførelse af typehuse, rækkehuse, villaer eller andre boligprojekter.</strong>
                </p>
              </div>

              <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
                Vi hjælper med at se mulighederne i grunden, udvikle projektet og skabe et realistisk grundlag for økonomi, byggeri og gennemførelse.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href="#vi-kan-hjaelpe-med"
                  className="inline-flex items-center gap-2 text-sm font-heading font-bold text-amber-800 hover:text-amber-900"
                >
                  <span>Gennemgå hvad vi kan hjælpe med</span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative group cursor-pointer" onClick={() => openLightbox(['/_assets/forside-rev-3_81597a46.png', '/_assets/bagside-rev-2_1570c7fa.png'], 0)}>
                <div className="overflow-hidden border border-stone-200 shadow-xl bg-stone-100">
                  <img
                    src={imageUrl('/_assets/forside-rev-3_81597a46.png', 'width=1000,quality=85,format=auto')}
                    alt="Projektudvikling af boligområde og moderne rækkehuse på Holbækvej"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-stone-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="bg-stone-900/90 text-white text-xs font-semibold px-4 py-2 flex items-center gap-2">
                      <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
                      Klik for at forstørre
                    </span>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 bg-stone-900 text-white p-5 border border-stone-800 shadow-xl max-w-xs hidden sm:block">
                  <p className="font-heading font-bold text-xs tracking-wide uppercase text-amber-400 mb-1">
                    G28 Totalentreprise
                  </p>
                  <p className="text-xs text-stone-300 leading-relaxed">
                    Fra de første analyser og tegninger til nøglefærdigt kvalitetsbyggeri.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* VI KAN HJÆLPE MED */}
      <section id="vi-kan-hjaelpe-med" className="py-20 sm:py-28 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-800">
              Ydelser & Kompetencer
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight uppercase">
              VI KAN HJÆLPE MED
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesList.map((service, idx) => {
              const IconComp = service.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white p-6 sm:p-7 border border-stone-200 shadow-sm hover:shadow-md hover:border-amber-700/50 transition-all flex flex-col justify-between group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-stone-100 group-hover:bg-amber-700 text-stone-900 group-hover:text-white flex items-center justify-center transition-colors shrink-0">
                        <IconComp className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <span className="text-xs font-bold text-stone-400">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>

                    <h3 className="font-heading font-bold text-lg text-stone-900 group-hover:text-amber-800 transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                      {service.desc}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-stone-100 mt-4 flex items-center gap-2 text-xs font-semibold text-stone-700">
                    <CheckCircle2 className="w-4 h-4 text-amber-700 shrink-0" aria-hidden="true" />
                    <span>Inkluderet i vores rådgivning & entreprise</span>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* FRA GRUND TIL FÆRDIGT PROJEKT */}
      <section className="py-20 sm:py-28 bg-stone-900 text-white border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-400">
                Helhedstænkning
              </div>
              
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight uppercase">
                FRA GRUND TIL FÆRDIGT PROJEKT
              </h2>
            </div>

            <div className="bg-stone-950 p-8 sm:p-10 border border-stone-800 space-y-6">
              <p className="font-heading font-bold text-xl sm:text-2xl text-amber-400 leading-snug">
                Vi ser på mere end selve byggeriet.
              </p>

              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Vi ser på, hvordan en grund eller ejendom bedst kan udvikles, hvilke muligheder der er, hvordan projektet kan optimeres, og hvordan idéen kan omsættes til et byggeri, der både fungerer i praksis og økonomisk.
              </p>
            </div>

            {/* Fremhævet CTA callout */}
            <div className="bg-gradient-to-r from-amber-900/30 to-stone-900 p-8 sm:p-10 border border-amber-600/40 space-y-6">
              <p className="font-heading font-bold text-lg sm:text-xl text-white leading-relaxed">
                Har du jord, en ejendom eller en idé til et boligprojekt, tager vi gerne den første snak om mulighederne.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href="#kontakt-sektion"
                  className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-sm sm:text-base px-6 py-3.5 transition-all shadow-md"
                >
                  <Send className="w-4 h-4" aria-hidden="true" />
                  <span>Start en uforpligtende snak</span>
                </a>

                <a
                  href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-700 text-white font-heading font-semibold text-sm sm:text-base px-6 py-3.5 border border-stone-700 transition-all"
                >
                  <PhoneCall className="w-4 h-4 text-amber-400" aria-hidden="true" />
                  <span>Ring til os på {SITE_INFO.phone}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* DEDIKERET SEKTION: AKTUELT PROJEKT & PROJEKTTEGNINGER */}
      <section id="aktuelt-projekt" className="py-20 sm:py-28 bg-stone-900 text-white border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-16">
          
          {/* Header */}
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Eksempel på projektudvikling</span>
            </div>
            
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight uppercase">
              HOLBÆKVEJ 301 – 28 MODERNE RÆKKEHUSE
            </h2>

            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              Her ses et konkret eksempel på et komplet udviklingsprojekt: Udstykning og bebyggelse af et 14.000 m² stort grundareal med 28 moderne étplans rækkehuse i gule mursten med sorte tage, integrerede udhuse, terrasser og grønne fællesarealer.
            </p>
          </div>

          {/* Nøgletal & Faktabar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 sm:p-8 bg-stone-950 border border-stone-800">
            <div className="space-y-1">
              <span className="text-xs text-stone-400 uppercase tracking-wider block">Boligenheder</span>
              <span className="font-heading font-extrabold text-2xl sm:text-3xl text-amber-400">28 stk.</span>
              <span className="text-xs text-stone-500 block">Ca. 129–133 m² pr. bolig</span>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-stone-400 uppercase tracking-wider block">Grundareal</span>
              <span className="font-heading font-extrabold text-2xl sm:text-3xl text-white">14.000 m²</span>
              <span className="text-xs text-stone-500 block">Heraf 2.617 m² friareal</span>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-stone-400 uppercase tracking-wider block">Samlet Boligareal</span>
              <span className="font-heading font-extrabold text-2xl sm:text-3xl text-white">3.645 m²</span>
              <span className="text-xs text-stone-500 block">30,6% bebyggelsesprocent</span>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-stone-400 uppercase tracking-wider block">Arkitekt & Rådgivning</span>
              <span className="font-heading font-extrabold text-lg sm:text-xl text-white">Meldrup Pugholm</span>
              <span className="text-xs text-stone-500 block">Arkitekt & Ingeniør</span>
            </div>
          </div>

          {/* 3D Visualiseringer (Forside & Bagside) */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                  3D-Visualiseringer af Bebyggelsen
                </h3>
                <p className="text-sm text-stone-400 mt-1">
                  Se facader mod det centrale vejrum samt de private terrasser mod de grønne fællesarealer.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {projectVisualizations.map((vis, idx) => (
                <div 
                  key={idx}
                  onClick={() => openLightbox(projectVisualizations.map(v => v.src), idx)}
                  className="group bg-stone-950 border border-stone-800 overflow-hidden cursor-pointer hover:border-amber-700/60 transition-all flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-stone-900">
                    <img
                      src={imageUrl(vis.src, 'width=1200,quality=85,format=auto')}
                      alt={vis.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3 bg-stone-900/90 text-amber-400 border border-amber-500/30 text-xs font-semibold px-3 py-1">
                      {vis.badge}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-stone-950/80 text-white text-xs px-2.5 py-1.5 flex items-center gap-1.5 opacity-90 group-hover:bg-amber-700 transition-colors">
                      <Eye className="w-3.5 h-3.5" />
                      <span>Forstør visualisering</span>
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                    <div>
                      <h4 className="font-heading font-bold text-lg text-white group-hover:text-amber-400 transition-colors">
                        {vis.title}
                      </h4>
                      <p className="text-stone-400 text-xs sm:text-sm mt-2 leading-relaxed">
                        {vis.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-stone-800 flex items-center gap-2 text-xs font-semibold text-amber-400">
                      <Maximize2 className="w-3.5 h-3.5" />
                      <span>Klik for at se i fuld skærm</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Projekttegninger (PDF Oversigt) */}
          <div className="space-y-6 pt-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-1">
                Byggeteknisk Dokumentation
              </div>
              <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
                Officielle Projekttegninger
              </h3>
              <p className="text-stone-400 text-sm sm:text-base max-w-3xl mt-2 leading-relaxed">
                Her kan du gennemgå de detaljerede byggetegninger, som danner grundlag for myndighedsbehandling, udstykning og den håndværksmæssige opførelse.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectDrawings.map((drawing) => (
                <div 
                  key={drawing.id}
                  className="bg-stone-950 border border-stone-800 hover:border-amber-700/60 p-6 flex flex-col justify-between transition-all group"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-heading font-extrabold text-xs tracking-wider">
                        <Ruler className="w-3 h-3" />
                        Tegning {drawing.id}
                      </span>
                      <span className="text-xs text-stone-400 font-mono">
                        {drawing.format}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-heading font-bold text-lg text-white group-hover:text-amber-400 transition-colors">
                        {drawing.title}
                      </h4>
                      <p className="text-stone-400 text-xs sm:text-sm leading-relaxed">
                        {drawing.description}
                      </p>
                    </div>

                    <ul className="space-y-1.5 pt-2">
                      {drawing.specs.map((spec, sIdx) => (
                        <li key={sIdx} className="flex items-center gap-2 text-xs text-stone-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-6 mt-6 border-t border-stone-800 flex items-center justify-between gap-3">
                    <a
                      href={drawing.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 flex-1 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-xs py-2.5 px-4 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>Åbn tegning (PDF)</span>
                      <ExternalLink className="w-3 h-3 text-amber-200" />
                    </a>

                    <a
                      href={drawing.fileUrl}
                      download
                      className="p-2.5 bg-stone-900 hover:bg-stone-800 text-stone-300 hover:text-white border border-stone-700 transition-colors"
                      title="Download PDF"
                      aria-label={`Download ${drawing.title}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* DEDIKERET SEKTION: TRÆKONSTRUKTIONER & INTERIØR (IMG_1359 - IMG_1363) */}
      <section className="py-20 sm:py-28 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-14">
          
          <div className="max-w-3xl space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-800">
              Håndværk & Bygninger i Træ
            </div>
            
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-tight uppercase">
              GENNEMFØRTE TRÆKONSTRUKTIONER & INTERIØRDETALJER
            </h2>

            <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
              Kvaliteten i et udviklingsprojekt ses i de færdige rammer. Vi forener robuste byggetekniske konstruktioner med unikt snedker- og tømrerhåndværk – fra fritlagte loftsbjælker og buede vinduespartier til specialfremstillede trækøkkener.
            </p>
          </div>

          {/* Photo Showcase Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timberPhotos.map((photo, idx) => (
              <div
                key={idx}
                onClick={() => openLightbox(timberPhotos.map(p => p.src), idx)}
                className={`group bg-stone-50 border border-stone-200 overflow-hidden cursor-pointer hover:shadow-xl hover:border-amber-700/50 transition-all flex flex-col ${
                  idx === 0 ? 'lg:col-span-2 lg:grid lg:grid-cols-2 lg:items-center' : ''
                }`}
              >
                <div className={`relative overflow-hidden bg-stone-200 ${idx === 0 ? 'h-full min-h-[320px]' : 'h-64'}`}>
                  <img
                    src={imageUrl(photo.src, 'width=1000,quality=85,format=auto')}
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-stone-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="bg-stone-900/90 text-white text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 shadow-md">
                      <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
                      Se stort billede
                    </span>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                  <div className="space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
                      G28 Håndværk
                    </span>
                    <h3 className="font-heading font-bold text-lg text-stone-900 group-hover:text-amber-800 transition-colors">
                      {photo.title}
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                      {photo.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-stone-200/80 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                    <Eye className="w-3.5 h-3.5" />
                    <span>Klik for at åbne i galleri</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* The 5 Phases (Process Breakdown) */}
      <section className="py-20 sm:py-28 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-800">
              Struktureret Proces
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight">
              De 5 faser i vores projektudvikling
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Et overskueligt og gennemskueligt forløb sikrer, at du altid kender næste skridt, tidsplanen og de økonomiske milepæle.
            </p>
          </div>

          {/* Tab buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 border-b border-stone-200 pb-4">
            {phases.map((phase, idx) => (
              <button
                key={phase.step}
                onClick={() => setActiveTab(idx)}
                className={`px-4 py-3 text-xs sm:text-sm font-heading font-bold transition-all border-b-2 -mb-[18px] cursor-pointer ${
                  activeTab === idx
                    ? 'border-amber-700 text-amber-900 bg-stone-50'
                    : 'border-transparent text-stone-500 hover:text-stone-900'
                }`}
              >
                Fase {phase.step}: {phase.title.split('&')[0]}
              </button>
            ))}
          </div>

          {/* Active Phase Card */}
          <div className="bg-stone-50 border border-stone-200 p-8 sm:p-12 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="font-heading font-extrabold text-3xl text-amber-800">
                    Fase {phases[activeTab].step}
                  </span>
                  <span className="text-stone-400">•</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-white px-2.5 py-1 border border-stone-200">
                    <Clock className="w-3.5 h-3.5 text-amber-700" aria-hidden="true" />
                    {phases[activeTab].duration}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900">
                  {phases[activeTab].title}
                </h3>

                <p className="text-stone-700 text-base leading-relaxed">
                  {phases[activeTab].description}
                </p>

                <div className="space-y-3 pt-2">
                  <h4 className="font-heading font-semibold text-xs uppercase tracking-wider text-stone-900">
                    Nøgleleverancer i denne fase:
                  </h4>
                  <ul className="space-y-2">
                    {phases[activeTab].deliverables.map((item, dIdx) => (
                      <li key={dIdx} className="flex items-start gap-3 text-sm text-stone-700">
                        <CheckCircle2 className="w-4 h-4 text-amber-800 shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="bg-white p-6 sm:p-8 border border-stone-200 space-y-5">
                  <div className="space-y-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                      Næste skridt
                    </span>
                    <p className="font-heading font-bold text-lg text-stone-900">
                      Klar til at sætte gang i din byggedrøm?
                    </p>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Vi tager gerne en uforpligtende samtale over en kop kaffe, hvor vi gennemgår din grund eller dine skitser.
                  </p>
                  <a
                    href="#kontakt-sektion"
                    className="inline-flex items-center justify-center gap-2 w-full bg-stone-900 hover:bg-stone-800 text-white font-heading font-semibold text-xs sm:text-sm py-3 px-4 transition-colors"
                  >
                    <span>Kontakt os angående projektet</span>
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Featured References & Development Cases */}
      <section className="py-20 sm:py-28 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl space-y-3">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-800">
                Udførte Projekter
              </div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight">
                Se hvad vi skaber i praksis
              </h2>
              <p className="text-stone-700 text-base leading-relaxed">
                Vores referencer taler for sig selv. Her er et udvalg af projekter, hvor vi har været med til at forme og bygge ekstraordinære rammer.
              </p>
            </div>
            <div>
              <Link
                to="/projekter"
                className="inline-flex items-center gap-2 font-heading font-bold text-amber-800 hover:text-amber-900 transition-colors"
              >
                <span>Se alle projekter</span>
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Case 1 */}
            <Link
              to="/projekter/arkitekttegnet-villa-vesterlundvej"
              className="group bg-white border border-stone-200 shadow-sm hover:shadow-xl transition-all flex flex-col overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden bg-stone-200">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/edit-20260901t094720-cfabfb3e-16bit.png', 'width=800,quality=85,format=auto')}
                  alt="Arkitekttegnet Villa på Vesterlundvej"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 bg-stone-900/90 text-white text-[11px] font-semibold px-2.5 py-1">
                  Vesterlundvej • 2024
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-stone-900 group-hover:text-amber-800 transition-colors">
                    Arkitekttegnet Villa i Cortenstål
                  </h3>
                  <p className="text-stone-600 text-xs sm:text-sm mt-2 line-clamp-2">
                    Opført i to niveauer med facader i rustfarvet cortenstål, trælameller, atriumgårdhave og panoramavinduer.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 pt-2">
                  Læs om projektet <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                </span>
              </div>
            </Link>

            {/* Case 2 */}
            <Link
              to="/projekter/sommerhus-hornbaek-til-og-ombygning"
              className="group bg-white border border-stone-200 shadow-sm hover:shadow-xl transition-all flex flex-col overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden bg-stone-200">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/edit-20260901t091033-116779d5-16bit.png', 'width=800,quality=85,format=auto')}
                  alt="Sommerhus i Hornbæk"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 bg-stone-900/90 text-white text-[11px] font-semibold px-2.5 py-1">
                  Hornbæk • 2025
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-stone-900 group-hover:text-amber-800 transition-colors">
                    Moderne Træsommerhus & Uderum
                  </h3>
                  <p className="text-stone-600 text-xs sm:text-sm mt-2 line-clamp-2">
                    Gennemgribende til- og ombygning med sort træbeklædning, sammenhængende terrasse og integreret havebassin.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 pt-2">
                  Læs om projektet <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                </span>
              </div>
            </Link>

            {/* Case 3 */}
            <Link
              to="/projekter/moderne-landejendom-stilling-skanderborg"
              className="group bg-white border border-stone-200 shadow-sm hover:shadow-xl transition-all flex flex-col overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden bg-stone-200">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/skilt-03-photoshop-rez_5e315522.jpg', 'width=800,quality=85,format=auto')}
                  alt="Moderne Landejendom i Stilling"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 bg-stone-900/90 text-white text-[11px] font-semibold px-2.5 py-1">
                  Stilling, Skanderborg • 2024
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-stone-900 group-hover:text-amber-800 transition-colors">
                    Moderne Landejendom i Naturlandskab
                  </h3>
                  <p className="text-stone-600 text-xs sm:text-sm mt-2 line-clamp-2">
                    Opført med mørk træbeklædning, karakterfuld skorsten og store glaspartier i harmoni med omgivelserne.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 pt-2">
                  Læs om projektet <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Direct Contact Callout */}
      <section className="bg-stone-900 text-white py-16 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 bg-stone-950 p-8 sm:p-12 border border-stone-800">
            <div className="space-y-3 max-w-2xl">
              <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
                Har du en grund eller en konkret byggeidé?
              </h3>
              <p className="text-stone-400 text-sm sm:text-base leading-relaxed">
                Vi tilbyder et uforpligtende screeningsmøde, hvor vi vurderer projektets muligheder, byggetekniske faldgruber og overordnede økonomi.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full sm:w-auto">
              <a
                href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-700 text-white font-heading font-bold text-sm px-6 py-3.5 border border-stone-700 transition-colors"
              >
                <PhoneCall className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <span>Ring {SITE_INFO.phone}</span>
              </a>
              <a
                href="#kontakt-sektion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-sm px-6 py-3.5 transition-colors shadow-md"
              >
                <Send className="w-4 h-4" aria-hidden="true" />
                <span>Skriv til os</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <div id="kontakt-sektion">
        <ContactSection 
          title="Lad os drøfte dit næste byggeprojekt" 
          subtitle="Udfyld formularen nedenfor med dine tanker eller ønsker til projektudvikling, så vender vi hurtigt tilbage for en uforpligtende dialog."
        />
      </div>

      {/* Gallery Lightbox Modal */}
      <GalleryLightboxModal
        images={lightboxImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
      />
    </>
  );
}
